import { Button, Card, CardBody, Chip, Skeleton } from '@nextui-org/react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ResourceInfo, SectionInfo } from '../types';
import { fetchResources, getDownloadUrl } from '../services/resourceService';
import { useAuth0 } from '@auth0/auth0-react';

const FileIcon: React.FC<{ type: string }> = ({ type }) => {
  const color =
    type === 'pdf'
      ? 'text-red-500'
      : type === 'zip'
        ? 'text-amber-500'
        : 'text-blue-500';
  return (
    <div className={`${color}`}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14,2 14,8 20,8" />
      </svg>
    </div>
  );
};

const LinkIcon: React.FC = () => (
  <div className="text-airlock-purple">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
      <polyline points="15,3 21,3 21,9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  </div>
);

const DateLabel: React.FC<{ timestamp: number }> = ({ timestamp }) => (
  <span className="text-xs text-slate-400">
    Updated{' '}
    {new Date(timestamp).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })}
  </span>
);

// Skeleton placeholder for preview area
const PreviewSkeleton: React.FC = () => (
  <div className="bg-slate-50 border-b border-slate-100 animate-pulse rounded-t-lg" style={{ height: '200px' }}>
    <div className="w-full h-full flex items-center justify-center">
      <div className="text-slate-300">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21,15 16,10 5,21" />
        </svg>
      </div>
    </div>
  </div>
);

// Card for images (png, jpg, jpeg) — shows full preview
const ImageResourceCard: React.FC<{
  resource: ResourceInfo;
  fileUrl: string | null;
  onDownload: (resource: ResourceInfo) => void;
  isDownloading: string | null;
}> = ({ resource, fileUrl, onDownload, isDownloading }) => {
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <Card className="bg-white border border-slate-200 shadow-none hover:border-slate-300 transition-colors overflow-hidden">
      {!fileUrl ? (
        <PreviewSkeleton />
      ) : (
        <div className="bg-slate-50 p-4 flex items-center justify-center border-b border-slate-100 rounded-t-lg relative" style={{ minHeight: '160px' }}>
          {!imgLoaded && (
            <div className="absolute inset-0 animate-pulse bg-slate-100" />
          )}
          <img
            src={fileUrl}
            alt={resource.title}
            className={`max-h-48 object-contain transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImgLoaded(true)}
          />
        </div>
      )}
      <CardBody className="p-4">
        <h4 className="text-sm font-semibold text-slate-800 mb-1">
          {resource.title}
        </h4>
        <div className="flex items-center gap-2 mb-3">
          <Chip size="sm" variant="flat" className="text-xs bg-slate-100 text-slate-500">
            .{resource.fileType.toUpperCase()}
          </Chip>
          <DateLabel timestamp={resource._creationTime} />
        </div>
        <Button
          size="sm"
          variant="bordered"
          className="border-slate-300 text-slate-600 w-full"
          radius="sm"
          onClick={() => onDownload(resource)}
          isLoading={isDownloading === resource._id}
        >
          Download
        </Button>
      </CardBody>
    </Card>
  );
};

// Card for PDFs — shows embedded preview
const PdfResourceCard: React.FC<{
  resource: ResourceInfo;
  fileUrl: string | null;
  onDownload: (resource: ResourceInfo) => void;
  isDownloading: string | null;
}> = ({ resource, fileUrl, onDownload, isDownloading }) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);

  return (
    <Card className="bg-white border border-slate-200 shadow-none hover:border-slate-300 transition-colors overflow-hidden">
      {!fileUrl ? (
        <PreviewSkeleton />
      ) : (
        <div className="bg-slate-50 border-b border-slate-100 rounded-t-lg relative overflow-hidden" style={{ height: '200px' }}>
          {!iframeLoaded && (
            <div className="absolute inset-0 animate-pulse bg-slate-100 flex items-center justify-center">
              <div className="text-slate-300">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14,2 14,8 20,8" />
                </svg>
              </div>
            </div>
          )}
          <iframe
            src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            title={resource.title}
            className={`w-full h-full border-0 transition-opacity duration-300 ${iframeLoaded ? 'opacity-100' : 'opacity-0'}`}
            style={{ pointerEvents: 'none', borderRadius: '8px 8px 0 0' }}
            onLoad={() => setIframeLoaded(true)}
          />
        </div>
      )}
      <CardBody className="p-4">
        <h4 className="text-sm font-semibold text-slate-800 mb-1">
          {resource.title}
        </h4>
        <div className="flex items-center gap-2 mb-3">
          <Chip size="sm" variant="flat" className="text-xs bg-slate-100 text-slate-500">
            .PDF
          </Chip>
          <DateLabel timestamp={resource._creationTime} />
        </div>
        <Button
          size="sm"
          variant="bordered"
          className="border-slate-300 text-slate-600 w-full"
          radius="sm"
          onClick={() => onDownload(resource)}
          isLoading={isDownloading === resource._id}
        >
          View & Download
        </Button>
      </CardBody>
    </Card>
  );
};

// Default card for zips, links, other types
const DefaultResourceCard: React.FC<{
  resource: ResourceInfo;
  onDownload: (resource: ResourceInfo) => void;
  isDownloading: string | null;
}> = ({ resource, onDownload, isDownloading }) => {
  const isLink = resource.resourceType === 'link' && resource.url;

  return (
    <Card className="bg-white border border-slate-200 shadow-none hover:border-slate-300 transition-colors">
      <CardBody className="p-4">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-slate-50 rounded-lg flex-shrink-0">
            {isLink ? <LinkIcon /> : <FileIcon type={resource.fileType} />}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-slate-800 mb-1 truncate">
              {resource.title}
            </h4>
            <div className="flex items-center gap-2">
              <Chip size="sm" variant="flat" className="text-xs bg-slate-100 text-slate-500">
                {isLink ? 'LINK' : `.${resource.fileType.toUpperCase()}`}
              </Chip>
              <DateLabel timestamp={resource._creationTime} />
            </div>
          </div>
          {isLink ? (
            <Button
              size="sm"
              variant="bordered"
              className="border-slate-300 text-slate-600 flex-shrink-0"
              radius="sm"
              onClick={() => window.open(resource.url, '_blank')}
            >
              Open
            </Button>
          ) : (
            <Button
              size="sm"
              variant="bordered"
              className="border-slate-300 text-slate-600 flex-shrink-0"
              radius="sm"
              onClick={() => onDownload(resource)}
              isLoading={isDownloading === resource._id}
            >
              Download
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

const Resources: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const [resources, setResources] = useState<ResourceInfo[]>([]);
  const [sections, setSections] = useState<SectionInfo[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [fileUrls, setFileUrls] = useState<Record<string, string>>({});

  const isAdmin =
    user && (user['https://api.airlock/roles'] || []).includes('admin');

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getAccessTokenSilently();
        const data = await fetchResources(token);
        const loadedResources: ResourceInfo[] = data.resources || [];
        setResources(loadedResources);
        setSections(data.sections || []);
        setIsLoaded(true);

        // Pre-fetch URLs for images and PDFs so we can show previews
        const previewable = loadedResources.filter(
          (r) =>
            r.storageId &&
            r.resourceType !== 'link' &&
            ['png', 'jpg', 'jpeg', 'pdf'].includes(r.fileType?.toLowerCase())
        );
        const urls: Record<string, string> = {};
        await Promise.all(
          previewable.map(async (r) => {
            try {
              const { url } = await getDownloadUrl(token, r.storageId);
              urls[r._id] = url;
            } catch {
              // skip if URL fetch fails
            }
          })
        );
        setFileUrls(urls);
      } catch (err: any) {
        setError(err.message);
        setIsLoaded(true);
      }
    };
    load();
  }, [getAccessTokenSilently]);

  const handleDownload = async (resource: ResourceInfo) => {
    try {
      setDownloading(resource._id);
      // Use cached URL if available
      const url = fileUrls[resource._id];
      if (url) {
        window.open(url, '_blank');
      } else {
        const token = await getAccessTokenSilently();
        const { url: freshUrl } = await getDownloadUrl(token, resource.storageId);
        window.open(freshUrl, '_blank');
      }
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloading(null);
    }
  };

  const sortedSections = [...sections].sort(
    (a, b) => a.displayOrder - b.displayOrder
  );

  const getResourcesForSection = (sectionName: string) =>
    resources
      .filter((r) => r.section === sectionName)
      .sort((a, b) => a.displayOrder - b.displayOrder);

  const isImage = (ft: string) => ['png', 'jpg', 'jpeg'].includes(ft?.toLowerCase());
  const isPdf = (ft: string) => ft?.toLowerCase() === 'pdf';

  const renderResourceCard = (resource: ResourceInfo) => {
    if (resource.resourceType === 'link') {
      return (
        <DefaultResourceCard
          key={resource._id}
          resource={resource}
          onDownload={handleDownload}
          isDownloading={downloading}
        />
      );
    }
    if (isImage(resource.fileType)) {
      return (
        <ImageResourceCard
          key={resource._id}
          resource={resource}
          fileUrl={fileUrls[resource._id] || null}
          onDownload={handleDownload}
          isDownloading={downloading}
        />
      );
    }
    if (isPdf(resource.fileType)) {
      return (
        <PdfResourceCard
          key={resource._id}
          resource={resource}
          fileUrl={fileUrls[resource._id] || null}
          onDownload={handleDownload}
          isDownloading={downloading}
        />
      );
    }
    return (
      <DefaultResourceCard
        key={resource._id}
        resource={resource}
        onDownload={handleDownload}
        isDownloading={downloading}
      />
    );
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Resources</h1>
          <p className="text-sm text-slate-500 mt-1">
            Download sales materials, product documentation, and brand assets
          </p>
        </div>
        {isAdmin && (
          <Button
            color="primary"
            className="bg-airlock-purple hover:bg-airlock-deep text-white font-medium"
            radius="sm"
            onClick={() => navigate('/resources/manage')}
          >
            Manage Resources
          </Button>
        )}
      </div>

      {error && (
        <Card className="mb-6 bg-red-50 border border-red-200 shadow-none">
          <CardBody className="p-4">
            <p className="text-sm text-red-700">{error}</p>
          </CardBody>
        </Card>
      )}

      <Skeleton isLoaded={isLoaded} className="rounded-lg">
        {sortedSections.map((section) => {
          const sectionResources = getResourcesForSection(section.name);
          if (sectionResources.length === 0) return null;
          return (
            <div key={section._id} className="mb-8">
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
                {section.name}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sectionResources.map(renderResourceCard)}
              </div>
            </div>
          );
        })}
        {isLoaded && resources.length === 0 && (
          <Card className="bg-white border border-slate-200 shadow-none">
            <CardBody className="p-8 text-center">
              <p className="text-slate-500 text-sm">No resources available yet.</p>
            </CardBody>
          </Card>
        )}
      </Skeleton>
    </div>
  );
};

export default Resources;
